import { Action } from "../frame/Action.js";
export { SchemaJSON };
/**
 * Схема JSON с расширенным набором свойств: `icon`, `actions`, `onSubmit`.
 * 
 * @see {@link ../product/SectionFormJSON.jsx}, {@link FormJSON.jsx} -
 * Генераторы форм.
 * @example
 * const schema =
 * {
 *   title: "Пример формы", // Заголовок секции.
 *   icon: "grid", // Иконка секции.
 *   properties: // Поля формы.
 *   {
 *     user_name: // Идентификатор поля.
 *     {
 *       title: "Имя пользователя", // Надпись.
 *       type: "string", // Тип поля.
 *       default: "user123" // Начальное значение
 *     }
 *   },
 *   required: ["user_name"], // Обязательные поля.
 *   actions: // Кнопки действий.
 *     [
 *       {
 *         type: "primary", // Основное действие, см. Button.
 *         name: "Отправить", // Надпись на кнопке.
 *         icon: "sent", // Имя иконки, см. Icon.
 *         onClick: // Обработчик нажатия (DataHandler)
 *           (data) => console.log(data.user_name)
 *         // Для основной кнопки можно не указывать.
 *         // Если не указано, будет выполнено onSubmit.
 *       }
 *     ],
 *   onSubmit: // Обработчик отправки данных (DataHandler)
 *     (data) => console.log(data.user_name)
 * };
 * @author Алексей Васьковский <avaskovskiy@umney.ru>
 */
class SchemaJSON {
  /**
   * Заголовок формы.
   * @type {string|undefined}
   */
  title;
  /**
   * Имя иконки.
   * @type {string}
   * @see {@link ../frame/Icon.jsx}
   */
  icon = "question";
  /**
   * Описание формы, общая информация.
   * @type {string|undefined}
   */
  description;
  /**
   * Поля формы как в схеме JSON.
   * @type {Object.<string, SchemaJSON~Property>}
   */
  properties = {};
  /**
   * Список обязательных для заполнения полей.
   * @type {string[]}
   */
  required = [];
  /**
   * Кнопки действий.
   * @type {Action[]}
   * @see {@link ../frame/Action.jsx}
   */
  actions = [
    new Action(
      {
        type: "primary",
        icon: "sent",
        name: "Отправить"
      })
  ];
  /**
   * Обработчик отправки данных.
   * @type {DataHandler|undefined}
   * @see {@link DataHandler.js}
   */
  onSubmit;
  /**
   * Копирует свойства объекта с валидацией.
   * @param {Object} properties - свойства объекта.
   */
  constructor(properties) {
    Object.assign(this, properties);
  }
  /**
   * @returns {string} Обязательный заголовок.
   */
  getTitle() {
    return this.title || this.description || "SchemaJSON.title";
  }
  /**
   * @returns {string} Дополнительное описание,
   * если оно не было использовано как заголовок,
   * или пустую строку, если оно было использовано или не было задано.
   */
  getDescription() {
    return this.title && this.description || "";
  }
  /**
   * @returns {Array.<SchemaJSON~Property>} - Список свойств.
   */
  getPropertyList() {
    const list = [];
    for (const field in this.properties) {
      const property = { field, ...this.properties[field] };
      property.required ??= this.required.includes(field);
      list.push(property);
    }
    return list;
  }
}
/**
 * Тип данных.
 * @enum {string}
 * @inner
 */
SchemaJSON.Type =
{
  STRING: "string",
  NUMBER: "number",
  INTEGER: "integer",
  OBJECT: "object",
  ARRAY: "array",
  BOOLEAN: "boolean",
  NULL: "null",
};
/**
 * Свойство в схеме JSON.
 * @typedef {Object} SchemaJSON~Property
 * @property {string} [field] - Идентификатор поля.
 * @property {SchemaJSON~Type|string} type - Тип свойства.
 * @property {string} [title] - Заголовок.
 * @property {string} [description] - Подробное описание.
 * @property {*} [default] - Значение по умолчанию.
 * @property {boolean} [required=false] - Обязательное для заполнения.
 * @property {Array} {enum} - Список допустимых значений.
 * @property {Array} {enumTitle} - Список названий для каждого значения.
 * @property {string} [placeholder] - Отображается в пустом поле.
 * @property {int} [rows] - Количество строк для `textarea`.
 * @property {int} [maxLength] - Максимальный размер в символах.
 * @property {int} [minLength] - Минимальный размер в символах.
 * @property {number} [minimum] - Минимальное значение.
 * @property {number} [maximum] - Максимальное значение.
 * @property {number} [multipleOf] - Шаг изменения значения.
 * @property {string} [$comment] - Комментарий.
 * @property {string} [style] - Стиль из `FormJSON.module.scss`.
 */
